/**
 * Domain Knowledge Tools — "Sinks Not Pipes"
 *
 * Pulls domain-specific knowledge OUT of LLM prompts, INTO deterministic TypeScript.
 * - stackwright_pro_list_collections
 * - stackwright_pro_resolve_data_strategy
 * - stackwright_pro_validate_workflow
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { readFileSync, existsSync } from 'fs';
import { join } from 'path';
import { jsonCoerce } from '../coerce.js';

// ---------------------------------------------------------------------------
// Tool 1: List Collections
// ---------------------------------------------------------------------------

export interface ListCollectionsInput {
  _cwd?: string;
}

export interface CollectionInfo {
  name: string;
  endpoint: string;
  type?: string;
}

/** Read collections from data-otter artifact (primary) or stackwright.yml (fallback). */
export function handleListCollections(input: ListCollectionsInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();

  const sources: Array<{ path: string; source: string; parse: (raw: string) => CollectionInfo[] }> =
    [
      {
        path: join(cwd, '.stackwright', 'artifacts', 'data-config.json'),
        source: 'data-config.json',
        parse: (raw) => {
          const parsed = JSON.parse(raw);
          if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            // Not a valid artifact — fall through to YAML fallback
            return [];
          }
          return extractCollectionsFromArtifact(parsed as Record<string, unknown>);
        },
      },
      {
        path: join(cwd, 'stackwright.yml'),
        source: 'stackwright.yml',
        parse: extractCollectionsFromYaml,
      },
    ];

  for (const { path, source, parse } of sources) {
    if (!existsSync(path)) continue;
    try {
      const collections = parse(readFileSync(path, 'utf8'));
      return {
        text: JSON.stringify({ collections, source, collectionCount: collections.length }),
        isError: false,
      };
    } catch {
      // Source exists but is unparseable — try next
    }
  }

  return {
    text: JSON.stringify({
      collections: [],
      source: 'none',
      collectionCount: 0,
      hint: 'Run API Otter and Data Otter first',
    }),
    isError: false,
  };
}

/** Pull collections from the data-otter's JSON artifact. */
function extractCollectionsFromArtifact(raw: Record<string, unknown>): CollectionInfo[] {
  const collections: CollectionInfo[] = [];
  const integrations = raw.integrations ?? raw.collections ?? [];
  if (!Array.isArray(integrations)) return collections;

  for (const item of integrations) {
    if (!item || typeof item !== 'object') continue;
    const obj = item as Record<string, unknown>;

    // Direct collection entry
    if (typeof obj.name === 'string' && typeof obj.endpoint === 'string') {
      collections.push(makeCollectionInfo(obj.name, obj.endpoint, obj.type));
      continue;
    }

    // Nested collections inside integration entry
    if (!Array.isArray(obj.collections)) continue;
    for (const col of obj.collections) {
      if (!col || typeof col !== 'object') continue;
      const c = col as Record<string, unknown>;
      if (typeof c.name === 'string' && typeof c.endpoint === 'string') {
        collections.push(makeCollectionInfo(c.name, c.endpoint, c.type ?? obj.type));
      }
    }
  }
  return collections;
}

function makeCollectionInfo(name: string, endpoint: string, type: unknown): CollectionInfo {
  return { name, endpoint, ...(typeof type === 'string' ? { type } : {}) };
}

/** Best-effort regex extraction from stackwright.yml integrations block. */
function extractCollectionsFromYaml(yamlText: string): CollectionInfo[] {
  const collections: CollectionInfo[] = [];
  const lines = yamlText.split('\n');

  let inIntegrations = false;
  let currentName: string | null = null;
  let currentEndpoint: string | null = null;
  let currentType: string | null = null;

  for (const line of lines) {
    if (line.length > 1000) continue; // Defense against ReDoS on pathological lines
    if (/^integrations:\s*$/.test(line)) {
      inIntegrations = true;
      continue;
    }
    if (inIntegrations && /^[a-z]/.test(line) && !line.startsWith(' ')) {
      inIntegrations = false;
    }
    if (!inIntegrations) continue;

    const stripQuotes = (s: string) => s.trim().replace(/^['"]|['"]$/g, '');

    const typeMatch = line.match(/^\s+type:\s*(.+)$/);
    if (typeMatch?.[1]) currentType = stripQuotes(typeMatch[1]);

    const nameMatch = line.match(/^[\s-]+name:\s*(.+)$/);
    if (nameMatch?.[1]) {
      if (currentName && currentEndpoint) {
        collections.push(makeCollectionInfo(currentName, currentEndpoint, currentType));
      }
      currentName = stripQuotes(nameMatch[1]);
      currentEndpoint = null;
    }

    const endpointMatch = line.match(/^\s+endpoint:\s*(.+)$/);
    if (endpointMatch?.[1]) currentEndpoint = stripQuotes(endpointMatch[1]);
  }

  if (currentName && currentEndpoint) {
    collections.push(makeCollectionInfo(currentName, currentEndpoint, currentType));
  }
  return collections;
}

// ---------------------------------------------------------------------------
// Tool 2: Resolve Data Strategy
// ---------------------------------------------------------------------------

export interface DataStrategy {
  strategy: string;
  mechanism: string;
  mechanismPackage?: string;
  revalidateSeconds?: number;
  pulse: boolean;
  requiredPackages: Record<string, string>;
  handoffFlags: string[];
  description: string;
}

const DATA_STRATEGIES: Record<string, DataStrategy> = {
  'pulse-fast': {
    strategy: 'pulse-fast',
    mechanism: 'Client-side polling via @stackwright-pro/pulse',
    mechanismPackage: '@stackwright-pro/pulse',
    pulse: true,
    requiredPackages: { '@stackwright-pro/pulse': 'latest', '@tanstack/react-query': '^5.0.0' },
    handoffFlags: ['PULSE_MODE=true'],
    description:
      'Real-time updates every few seconds. Uses client-side polling. Dashboard Otter should use *_pulse component variants.',
  },
  'isr-fast': {
    strategy: 'isr-fast',
    mechanism: 'Next.js ISR',
    revalidateSeconds: 60,
    pulse: false,
    requiredPackages: {},
    handoffFlags: [],
    description:
      'Near real-time with 60-second ISR revalidation. Good for dashboards that need minute-level freshness.',
  },
  'isr-standard': {
    strategy: 'isr-standard',
    mechanism: 'Next.js ISR',
    revalidateSeconds: 3600,
    pulse: false,
    requiredPackages: {},
    handoffFlags: [],
    description: 'Standard hourly revalidation. Good for most API-backed pages.',
  },
  'isr-slow': {
    strategy: 'isr-slow',
    mechanism: 'Next.js ISR',
    revalidateSeconds: 86400,
    pulse: false,
    requiredPackages: {},
    handoffFlags: [],
    description: 'Daily revalidation. Good for infrequently changing data.',
  },
};

export interface ResolveDataStrategyInput {
  strategy: string;
}

/** Look up the data freshness strategy. Replaces the markdown table in the data-otter prompt. */
export function handleResolveDataStrategy(input: ResolveDataStrategyInput): {
  text: string;
  isError: boolean;
} {
  const key = input.strategy.trim().toLowerCase();
  const match = DATA_STRATEGIES[key];

  if (!match) {
    const validKeys = Object.keys(DATA_STRATEGIES).join(', ');
    return {
      text: JSON.stringify({
        error: true,
        message: `Unknown strategy: "${input.strategy}". Valid strategies: ${validKeys}`,
        validStrategies: Object.keys(DATA_STRATEGIES),
      }),
      isError: true,
    };
  }

  // Hint: the exact tool call the otter should make next
  const configureIsrCall = match.pulse
    ? null
    : {
        tool: 'stackwright_pro_configure_isr_batch',
        args: {
          collections: [{ name: '$COLLECTION', revalidateSeconds: match.revalidateSeconds }],
        },
      };

  return {
    text: JSON.stringify({ ...match, configureIsrCall }),
    isError: false,
  };
}

// ---------------------------------------------------------------------------
// Tool 3: Validate Workflow
// ---------------------------------------------------------------------------

export interface WorkflowValidationIssue {
  code: string;
  message: string;
  path?: string;
}

export interface ValidateWorkflowInput {
  workflow?: Record<string, unknown>;
  _cwd?: string;
}

/** Shorthand for returning a validation failure. */
function fail(errors: WorkflowValidationIssue[]): { text: string; isError: boolean } {
  return { text: JSON.stringify({ valid: false, errors, warnings: [] }), isError: true };
}

/**
 * Inline workflow validation (Approach B) — no cross-package import.
 * CLAUDE.md invariant 1 forbids runtime imports from @stackwright-pro/workflow.
 */
export function handleValidateWorkflow(input: ValidateWorkflowInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();
  let raw: Record<string, unknown>;

  // Resolve: explicit object > artifact file
  if (input.workflow && Object.keys(input.workflow).length > 0) {
    raw = input.workflow;
  } else {
    const artifactPath = join(cwd, '.stackwright', 'artifacts', 'workflow-config.json');
    if (!existsSync(artifactPath)) {
      return fail([
        {
          code: 'NO_WORKFLOW',
          message:
            'No workflow provided and .stackwright/artifacts/workflow-config.json not found. Pass a workflow object or run the workflow otter first.',
        },
      ]);
    }
    try {
      raw = JSON.parse(readFileSync(artifactPath, 'utf8'));
    } catch (err) {
      return fail([{ code: 'INVALID_JSON', message: `Failed to parse workflow artifact: ${err}` }]);
    }
  }

  // Unwrap { workflow: { ... } } envelope if present
  const workflow = (
    raw.workflow && typeof raw.workflow === 'object' ? raw.workflow : raw
  ) as Record<string, unknown>;

  const errors: WorkflowValidationIssue[] = [];
  const warnings: WorkflowValidationIssue[] = [];

  // Rule 1: workflow.id
  if (typeof workflow.id !== 'string' || !workflow.id) {
    errors.push({ code: 'MISSING_ID', message: 'workflow.id is required', path: 'workflow.id' });
  } else if (!/^[a-z0-9-]+$/.test(workflow.id)) {
    errors.push({
      code: 'INVALID_ID',
      message: `workflow.id "${workflow.id}" must match ^[a-z0-9-]+$`,
      path: 'workflow.id',
    });
  }

  // Rule 2: workflow.label
  if (typeof workflow.label !== 'string' || !workflow.label) {
    errors.push({
      code: 'MISSING_LABEL',
      message: 'workflow.label is required',
      path: 'workflow.label',
    });
  }

  // Rule 3: steps array
  const steps = workflow.steps;
  if (!Array.isArray(steps)) {
    errors.push({
      code: 'MISSING_STEPS',
      message: 'workflow.steps must be an array',
      path: 'workflow.steps',
    });
    return { text: JSON.stringify({ valid: false, errors, warnings }), isError: false };
  }

  if (steps.length < 2) {
    errors.push({
      code: 'TOO_FEW_STEPS',
      message: 'A workflow must have at least 2 steps',
      path: 'workflow.steps',
    });
  }

  // Build step ID index
  const stepIds = new Set<string>();
  const duplicateIds: string[] = [];

  for (const step of steps) {
    if (!step || typeof step !== 'object') continue;
    const id = (step as Record<string, unknown>).id;
    if (typeof id !== 'string' || !id) {
      errors.push({
        code: 'MISSING_STEP_ID',
        message: 'Every step must have an id',
        path: 'workflow.steps',
      });
      continue;
    }
    if (!/^[a-z0-9_]+$/.test(id)) {
      errors.push({
        code: 'INVALID_STEP_ID',
        message: `Step ID "${id}" must match ^[a-z0-9_]+$`,
        path: `workflow.steps[${id}].id`,
      });
    }
    if (stepIds.has(id)) duplicateIds.push(id);
    stepIds.add(id);
  }

  if (duplicateIds.length > 0) {
    errors.push({
      code: 'DUPLICATE_STEP_IDS',
      message: `Duplicate step IDs: ${duplicateIds.join(', ')}`,
      path: 'workflow.steps',
    });
  }

  // Rule 4: initial_step references existing step
  const initialStep = workflow.initial_step;
  if (typeof initialStep !== 'string' || !initialStep) {
    errors.push({
      code: 'MISSING_INITIAL_STEP',
      message: 'workflow.initial_step is required',
      path: 'workflow.initial_step',
    });
  } else if (!stepIds.has(initialStep)) {
    errors.push({
      code: 'INVALID_INITIAL_STEP',
      message: `initial_step "${initialStep}" does not reference an existing step. Valid: ${[...stepIds].join(', ')}`,
      path: 'workflow.initial_step',
    });
  }

  // Rules 5+6: transition targets + service: warnings
  for (const step of steps) {
    if (!step || typeof step !== 'object') continue;
    const s = step as Record<string, unknown>;
    const stepId = String(s.id ?? '??');
    validateTransitionTargets(s, stepId, stepIds, errors);
    collectServiceWarnings(s, stepId, warnings);
  }

  // Workflow-level persistence service check
  if (typeof workflow.persistence === 'string' && workflow.persistence.startsWith('service:')) {
    warnings.push({
      code: 'WARN_SERVICE_REFERENCE',
      message:
        'service: reference at "workflow.persistence" requires @stackwright-pro/services. Prism mock fallback will be used until services layer is configured.',
      path: 'workflow.persistence',
    });
  }

  // Rule 7: at least one terminal step
  const hasTerminal = steps.some((step: unknown) => {
    if (!step || typeof step !== 'object') return false;
    return (step as Record<string, unknown>).type === 'terminal';
  });
  if (!hasTerminal) {
    errors.push({
      code: 'NO_TERMINAL_STATE',
      message: 'Workflow must have at least one step with type: terminal',
      path: 'workflow.steps',
    });
  }

  return { text: JSON.stringify({ valid: errors.length === 0, errors, warnings }), isError: false };
}

/** Check all transition targets within a step reference real step IDs. */
function validateTransitionTargets(
  step: Record<string, unknown>,
  stepId: string,
  stepIds: Set<string>,
  errors: WorkflowValidationIssue[]
): void {
  const check = (target: unknown, path: string) => {
    if (typeof target === 'string' && !stepIds.has(target)) {
      errors.push({
        code: 'ORPHANED_TRANSITION',
        message: `Step "${stepId}" transitions to "${target}" which does not exist`,
        path,
      });
    }
  };

  // on_submit.transition
  const onSubmit = step.on_submit as Record<string, unknown> | undefined;
  if (onSubmit && typeof onSubmit === 'object') {
    check(onSubmit.transition, `workflow.steps[${stepId}].on_submit.transition`);
  }

  // actions[].transition
  if (Array.isArray(step.actions)) {
    for (const action of step.actions) {
      if (!action || typeof action !== 'object') continue;
      const a = action as Record<string, unknown>;
      check(a.transition, `workflow.steps[${stepId}].actions[${a.id ?? '??'}].transition`);
    }
  }

  // conditions[].then.transition
  if (Array.isArray(step.conditions)) {
    for (const cond of step.conditions) {
      if (!cond || typeof cond !== 'object') continue;
      const then = (cond as Record<string, unknown>).then as Record<string, unknown> | undefined;
      if (then && typeof then === 'object') {
        check(then.transition, `workflow.steps[${stepId}].conditions`);
      }
    }
  }

  // show_fields_from
  if (Array.isArray(step.show_fields_from)) {
    for (const ref of step.show_fields_from)
      check(ref, `workflow.steps[${stepId}].show_fields_from`);
  }

  // display.source_step / display.source_steps
  const display = step.display as Record<string, unknown> | undefined;
  if (display && typeof display === 'object') {
    check(display.source_step, `workflow.steps[${stepId}].display.source_step`);
    if (Array.isArray(display.source_steps)) {
      for (const ref of display.source_steps)
        check(ref, `workflow.steps[${stepId}].display.source_steps`);
    }
  }
}

/** Collect service: references as warnings (non-blocking). */
function collectServiceWarnings(
  step: Record<string, unknown>,
  stepId: string,
  warnings: WorkflowValidationIssue[]
): void {
  const isService = (val: unknown): val is string =>
    typeof val === 'string' && val.startsWith('service:');

  const warn = (path: string) => {
    warnings.push({
      code: 'WARN_SERVICE_REFERENCE',
      message: `service: reference at "${path}" requires @stackwright-pro/services. Prism mock fallback will be used until services layer is configured.`,
      path,
    });
  };

  const onSubmit = step.on_submit as Record<string, unknown> | undefined;
  if (onSubmit && typeof onSubmit === 'object' && isService(onSubmit.action))
    warn(`${stepId}.on_submit.action`);

  const onEnter = step.on_enter as Record<string, unknown> | undefined;
  if (onEnter && typeof onEnter === 'object' && isService(onEnter.action))
    warn(`${stepId}.on_enter.action`);

  if (Array.isArray(step.actions)) {
    for (const action of step.actions) {
      if (!action || typeof action !== 'object') continue;
      const a = action as Record<string, unknown>;
      if (isService(a.action)) warn(`${stepId}.actions.${a.id ?? '??'}.action`);
    }
  }

  if (Array.isArray(step.fields)) {
    for (const field of step.fields) {
      if (!field || typeof field !== 'object') continue;
      const f = field as Record<string, unknown>;
      if (isService(f.data_source)) warn(`${stepId}.fields.${f.name ?? '??'}.data_source`);
    }
  }
}

// ---------------------------------------------------------------------------
// MCP Tool Registration
// ---------------------------------------------------------------------------

export function registerDomainTools(server: McpServer): void {
  const res = (r: { text: string; isError: boolean }) => ({
    content: [{ type: 'text' as const, text: r.text }],
    isError: r.isError,
  });

  server.tool(
    'stackwright_pro_list_collections',
    'List API-backed collections available for page generation. ' +
      'Reads from Data Otter artifact (.stackwright/artifacts/data-config.json) or stackwright.yml. ' +
      'Call this before generating pages that bind to data.',
    {},
    async () => res(handleListCollections({}))
  );

  server.tool(
    'stackwright_pro_resolve_data_strategy',
    "Look up the data freshness strategy configuration from the user's answer. " +
      'Returns mechanism, revalidation seconds, required packages, and the exact MCP tool call to make. ' +
      'Replaces the strategy table in the data-otter prompt.',
    {
      strategy: z
        .string()
        .describe(
          'The data-1 answer value: "pulse-fast", "isr-fast", "isr-standard", or "isr-slow"'
        ),
    },
    async ({ strategy }) => res(handleResolveDataStrategy({ strategy }))
  );

  server.tool(
    'stackwright_pro_validate_workflow',
    'Validate a workflow definition against the Stackwright workflow schema. ' +
      'Checks step ID uniqueness, transition targets, terminal state existence, and service references. ' +
      'Call this after the workflow otter produces output.',
    {
      workflow: jsonCoerce(z.record(z.string(), z.unknown()).optional()).describe(
        'Parsed workflow object. If omitted, reads from .stackwright/artifacts/workflow-config.json'
      ),
    },
    async ({ workflow }) => res(handleValidateWorkflow(workflow ? { workflow } : {}))
  );
}
